import { Injectable, OnDestroy } from '@angular/core';
import { AnalyticsService } from '@common/services/analytics.service';
import { UserService } from '@modules/auth/services';
import { Subscription } from 'rxjs';

import { OnboardingPhase, OnboardingTask } from '../interfaces/onboarding.interfaces';
import { TASK_MODULE_MAP } from './onboarding.config';
import { OnboardingService } from './onboarding.service';

/**
 * `task_id` → nombre de "step" del PRD de PostHog. La Fase 1 usa los nombres explícitos del ticket; el
 * resto cae al valor de `TASK_MODULE_MAP` (`gastos/canales/combos/usuarios/caja/devoluciones`, etc.) y, si
 * no está mapeado, al propio `task_id`.
 */
const ANALYTICS_STEP_NAME: Record<string, string> = {
    add_first_product: 'inventory',
    create_and_fund_account: 'bank_account',
    make_first_sale: 'first_sale',
};

export type BlackoutTrigger = 'auto' | 'banner_click' | 'checklist_click';
export type ChecklistTrigger = 'banner_button' | 'navbar_bar' | 'card_click';

/**
 * Key de `localStorage` de la antigua red de seguridad de `onboarding_completed` (cierre al cerrar el modal de la
 * Fase 3). Desde FINA-3377 el evento sale del claim atómico del modal final, así que la key solo se BORRA al
 * arrancar para no re-disparar un cierre de 3 fases que hubiera quedado pendiente en el navegador.
 */
const LEGACY_PENDING_COMPLETION_KEY = 'fina_onb_pending_completion';

/**
 * Servicio central de instrumentación PostHog del onboarding. Centraliza:
 *  - el **gating por fase** (`onboarding_phase_{n}` vía PostHog feature flag) — si el flag está OFF, ningún
 *    evento de esa fase se dispara;
 *  - las props comunes (`days_since_registration`, nombre de step, nº de fase);
 *  - el timing `time_to_complete_minutes` (desde que el blackout se mostró hasta que la tarea se completa);
 *  - el conteo de skips por fase.
 *
 * Los componentes llaman métodos semánticos (no tocan `posthog`). Se suscribe internamente a los streams
 * `taskCompleted$`/`phaseCompleted$`/`completion$` de `OnboardingService` para los eventos derivados del estado.
 * `providedIn: 'root'` (singleton); cleanup de sus suscripciones en `ngOnDestroy`.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingAnalyticsService implements OnDestroy {
    private readonly _subs = new Subscription();

    // task_id → timestamp (ms) en que se mostró su blackout, para calcular time_to_complete_minutes.
    private readonly _shownAt = new Map<string, number>();

    // Skips acumulados por número de fase (para skipped_steps_count del evento phase_{n}_completed).
    private readonly _skips: Record<number, number> = { 1: 0, 2: 0 };

    constructor(private analytics: AnalyticsService, private userService: UserService, private onboarding: OnboardingService) {
        // onboarding_step_completed (con timing): stream nuevo de tareas que cruzan a isCompleted=true.
        this._subs.add(
            this.onboarding.taskCompleted$.subscribe(({ task, phase }) => {
                const startedAt = this._shownAt.get(task.task_id);
                const minutes = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 60000)) : 0;
                this._shownAt.delete(task.task_id);
                this.capture(phase.order, 'onboarding_step_completed', {
                    step: this.stepName(task.task_id),
                    phase: phase.order,
                    time_to_complete_minutes: minutes,
                });
            }),
        );

        // onboarding_phase_{n}_completed: fase que cruza a 100%. Además, el modal de celebración que aparece en
        // ese momento ES el "blackout de transición" del AC (no existe un overlay aparte): si la fase NO es la
        // última, emitimos `onboarding_phase_transition_shown` (from→to). La última no tiene transición: el cierre
        // del onboarding se mide con `onboarding_completed` (ver `completion$` abajo).
        this._subs.add(
            this.onboarding.phaseCompleted$.subscribe(phase => {
                this.capture(phase.order, `onboarding_phase_${phase.order}_completed`, {
                    days_to_complete: this.daysSinceRegistration(),
                    tasks_completed: phase.tasks_done,
                    tasks_total: phase.tasks_total,
                    skipped_steps_count: this._skips[phase.order] ?? 0,
                });

                if (!this.isLastPhase(phase.order)) {
                    this.capture(phase.order, 'onboarding_phase_transition_shown', {
                        from_phase: phase.order,
                        to_phase: phase.order + 1,
                        trigger: 'auto',
                    });
                }
            }),
        );

        // onboarding_completed (FINA-3377): sale cuando `completion$` emite, es decir, cuando el claim atómico
        // confirmó que ESTA sesión muestra el modal final. Por eso se envía una sola vez por tenant (aunque haya
        // varias sesiones o recargas) y no hace falta una red de seguridad en localStorage. Trigger: cierre de la
        // Fase 2 (natural) o primer acceso tras la migración desde la Fase 3 (`migrated: true`).
        this._subs.add(
            this.onboarding.completion$.subscribe(({ completion }) => {
                const lastPhaseOrder = Math.max(0, ...(this.onboarding.currentState?.phases ?? []).map(p => p.order));
                this.capture(lastPhaseOrder, 'onboarding_completed', {
                    tenant_id: this.userService.tenant$.value?.tenantId ?? null,
                    days_to_complete: completion.daysToComplete,
                    rubro: completion.rubro,
                    migrated: completion.source === 'migrated',
                    total_days: this.daysSinceRegistration(),
                    phase_1_skipped: (this._skips[1] ?? 0) > 0,
                    phase_2_skipped: (this._skips[2] ?? 0) > 0,
                });
            }),
        );

        // Limpieza única de la antigua red de seguridad (cierre de 3 fases): no se re-dispara nada.
        this.clearLegacyPendingCompletion();
    }

    ngOnDestroy(): void {
        this._subs.unsubscribe();
    }

    /**
     * Limpia el estado transitorio por-sesión: timers de blackout (`_shownAt`) y conteo de skips (`_skips`).
     * El servicio es singleton (`providedIn: 'root'`) y sobrevive al logout→login en la misma pestaña, así que
     * sin este reset las métricas del usuario A se filtrarían al usuario B: `time_to_complete_minutes` incluiría
     * el tiempo de logout+login (valor absurdo) y `skipped_steps_count` arrastraría los skips de A. Se invoca en
     * el logout desde `AuthService.logout()`, junto a `OnboardingService.teardown()`. (No toca las suscripciones
     * a `taskCompleted$`/`phaseCompleted$`, que viven mientras la app exista y se limpian en `ngOnDestroy`.)
     */
    reset(): void {
        this._shownAt.clear();
        this._skips[1] = 0;
        this._skips[2] = 0;
    }

    /**
     * El modal de celebración se cerró (cualquier vía: CTA o ESC). Desde FINA-3377 `onboarding_completed` ya no
     * depende del cierre del modal (sale del claim atómico vía `completion$`), así que no hay nada que medir aquí.
     * Se mantiene el método porque el shell (`AppComponent`) sigue reenviando la señal.
     */
    celebrationClosed(): void {
        // Intencionalmente vacío (ver doc).
    }

    // ─────────────── API pública para componentes/servicios ───────────────

    /** Banner de fase renderizado (proxy de "viewed"). */
    bannerViewed(phase: OnboardingPhase): void {
        this.capture(phase.order, 'onboarding_banner_viewed', {
            phase: phase.order,
            steps_completed: phase.tasks_done,
            tasks_completed: phase.tasks_done,
            tasks_total: phase.tasks_total,
            days_since_registration: this.daysSinceRegistration(),
        });
    }

    /**
     * Click en una tarjeta/tarea del banner o del checklist. Incluye `tab` (obligatorias | opcionales) del AC
     * de Fase 2: se deriva del propio `is_mandatory` de la tarea (fuente de verdad de a qué pestaña pertenece),
     * así el valor es correcto tanto desde el checklist con pestañas como desde el banner/navbar sin pestañas.
     */
    stepClicked(task: OnboardingTask): void {
        const phase = this.phaseOrderForTask(task.task_id);
        const tab: 'obligatorias' | 'opcionales' = task.is_mandatory ? 'obligatorias' : 'opcionales';
        this.capture(phase, 'onboarding_step_clicked', { step_name: this.stepName(task.task_id), phase, tab });
    }

    /** Apertura del modal de detalle (checklist). */
    checklistOpened(trigger: ChecklistTrigger): void {
        const phase = this.onboarding.getActivePhase()?.order ?? 0;
        this.capture(phase, 'onboarding_checklist_opened', { phase, trigger });
    }

    /** Click en la barra de progreso del navbar. */
    navbarBarClicked(completionPercentage: number): void {
        const phase = this.onboarding.getActivePhase()?.order ?? 0;
        this.capture(phase, 'onboarding_navbar_bar_clicked', { phase, completion_percentage: completionPercentage });
    }

    /** El blackout de una tarea se mostró (arranca el timer de time_to_complete). */
    blackoutShown(taskId: string, trigger: BlackoutTrigger = 'auto'): void {
        this._shownAt.set(taskId, Date.now());
        const phase = this.phaseOrderForTask(taskId);
        this.capture(phase, 'onboarding_blackout_shown', { step: this.stepName(taskId), phase, trigger });
    }

    /** El usuario saltó el blackout de una tarea. */
    stepSkipped(taskId: string): void {
        const phase = this.phaseOrderForTask(taskId);
        if (phase) this._skips[phase] = (this._skips[phase] ?? 0) + 1;
        this.capture(phase, 'onboarding_step_skipped', { step: this.stepName(taskId), phase });
    }

    /**
     * El Blackout OMITIÓ automáticamente un paso del tour porque su elemento objetivo no apareció en el DOM
     * dentro del tope de espera (FINA-3295: con la API lenta el objetivo puede pintarse después del tope).
     * Antes la omisión era silenciosa (solo un console.warn); este evento permite medir en PostHog cuántos
     * pasos se pierden por latencia y sobre qué selector. NO es un skip del usuario: no toca `_skips` ni el
     * embudo de abandono.
     */
    stepAutoSkipped(taskId: string, selector: string, stepIndex: number): void {
        const phase = this.phaseOrderForTask(taskId);
        this.capture(phase, 'onboarding_step_auto_skipped', { step: this.stepName(taskId), phase, selector, step_index: stepIndex });
    }

    /**
     * El usuario COMPLETÓ el tour guiado de una tarea (botón "Finalizar" del último paso). Contraparte de
     * `stepSkipped`: permite distinguir un tour terminado con éxito de uno saltado (AC FINA-2633). No cuenta
     * como skip. Distinto de `onboarding_step_completed` (que marca la tarea real completada, no el tour).
     */
    tourCompleted(taskId: string): void {
        const phase = this.phaseOrderForTask(taskId);
        this.capture(phase, 'onboarding_tour_completed', { step: this.stepName(taskId), phase });
    }

    // ─────────────── Privados ───────────────

    /** Envía a PostHog SOLO si el feature flag de la fase está activo (gating del AC). */
    private capture(phase: number, event: string, props: Record<string, any>): void {
        if (!phase || !this.analytics.getExperimentVariant(`onboarding_phase_${phase}`)) return;
        this.analytics.analyticsCapture(event, props);
    }

    private stepName(taskId: string): string {
        return ANALYTICS_STEP_NAME[taskId] ?? TASK_MODULE_MAP[taskId] ?? taskId;
    }

    /** ¿`order` es la ÚLTIMA fase del onboarding (la de mayor `order` en el estado actual)? */
    private isLastPhase(order: number): boolean {
        const orders = (this.onboarding.currentState?.phases ?? []).map(p => p.order);
        const maxOrder = orders.length ? Math.max(...orders) : 0;
        return maxOrder > 0 && order >= maxOrder;
    }

    /** Nº de fase (order) que contiene la tarea, según el estado actual; 0 si no se encuentra. */
    private phaseOrderForTask(taskId: string): number {
        const state = this.onboarding.currentState;
        if (!state) return 0;
        return state.phases.find(phase => phase.tasks.some(task => task.task_id === taskId))?.order ?? 0;
    }

    /** Días enteros desde la creación del tenant (0 si no hay fecha). */
    private daysSinceRegistration(): number {
        const createdAt = this.userService.tenant$.value?.createdAt;
        if (!createdAt) return 0;
        return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000));
    }

    // ─────────────── Limpieza de la red de seguridad legacy (localStorage) ───────────────

    /** Borra la key del cierre pendiente de 3 fases. Best-effort: no rompe si `localStorage` no está disponible. */
    private clearLegacyPendingCompletion(): void {
        try {
            localStorage.removeItem(LEGACY_PENDING_COMPLETION_KEY);
        } catch {
            // localStorage inaccesible (modo privado / cuota / SSR): no hay nada que limpiar → se ignora a propósito.
        }
    }
}
